import { FeatureSection } from "./FeatureSection";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Brain, Zap, Search, Calculator, FileSearch, CheckCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export const AgenticAISection = () => {
  const { t } = useTranslation();
  const { ref: visualRef, isVisible: visualVisible } = useScrollAnimation({ threshold: 0.15 });

  return (
    <FeatureSection id="agentic-ai" background="default">
      <div className="max-w-4xl mx-auto space-y-12">
        {/* Centered Copy */}
        <div className="text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-xs font-medium">
            <Brain className="h-3.5 w-3.5" />
            {t("landing.agenticAI.badge")}
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
            {t("landing.agenticAI.title")}{" "}
            <span className="text-gradient">{t("landing.agenticAI.titleHighlight")}</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("landing.agenticAI.description")}
          </p>
        </div>

        {/* Visual - Full Width Tool Invocation Demo */}
        <div
          ref={visualRef}
          className={cn(
            "animate-scale-in",
            visualVisible && "is-visible"
          )}
        >
          <div className="w-full bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border/50 bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-cyan-500/10">
                  <Brain className="h-4 w-4 text-cyan-500" />
                </div>
                <span className="text-sm font-medium">{t("landing.agenticAI.demo.agentReasoning")}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Zap className="h-3.5 w-3.5 text-yellow-500" />
                {t("landing.agenticAI.demo.deepMode")}
              </div>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {/* Thinking indicator */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span>{t("landing.agenticAI.demo.analyzing")}</span>
              </div>

              {/* Tool invocations */}
              <div className={cn(
                "space-y-3 animate-stagger",
                visualVisible && "is-visible"
              )}>
                <ToolCard
                  icon={Search}
                  name="hybrid_search"
                  input='query: "Q4 revenue targets"'
                  status="completed"
                  output={t("landing.agenticAI.demo.toolSearchOutput")}
                />
                <ToolCard
                  icon={FileSearch}
                  name="get_meeting_analytics"
                  input='meeting_id: "abc-123"'
                  status="completed"
                  output={t("landing.agenticAI.demo.toolAnalyticsOutput")}
                />
                <ToolCard
                  icon={Calculator}
                  name="summarize_findings"
                  input="documents: [doc_1, doc_2, doc_3]"
                  status="running"
                />
              </div>

              {/* Response preview */}
              <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border/50">
                <p className="text-sm text-foreground">
                  {t("landing.agenticAI.demo.responsePreview")}
                </p>
                <span className="inline-block w-2 h-4 bg-primary/50 ml-1 animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Feature pills */}
        <div className="flex flex-wrap justify-center gap-3">
          <FeaturePill icon={Search} text={t("landing.agenticAI.features.multiTool")} />
          <FeaturePill icon={Brain} text={t("landing.agenticAI.features.chainOfThought")} />
          <FeaturePill icon={Zap} text={t("landing.agenticAI.features.realTimeStreaming")} />
        </div>
      </div>
    </FeatureSection>
  );
};

const ToolCard = ({
  icon: Icon,
  name,
  input,
  status,
  output,
}: {
  icon: React.ElementType;
  name: string;
  input: string;
  status: "running" | "completed";
  output?: string;
}) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
    <div className={cn(
      "p-2 rounded-lg shrink-0",
      status === "completed" ? "bg-green-500/10" : "bg-primary/10"
    )}>
      <Icon className={cn(
        "h-4 w-4",
        status === "completed" ? "text-green-500" : "text-primary"
      )} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium text-foreground">{name}</span>
        {status === "completed" ? (
          <CheckCircle className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
        )}
      </div>
      <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">{input}</div>
      {output && (
        <div className="text-xs text-green-600 dark:text-green-400 mt-1">{output}</div>
      )}
    </div>
  </div>
);

const FeaturePill = ({ icon: Icon, text }: { icon: React.ElementType; text: string }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted border border-border text-sm text-muted-foreground">
    <Icon className="h-4 w-4" />
    <span>{text}</span>
  </div>
);
